import type { ComputedRef, WritableComputedRef } from 'vue'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useChangelogApi, useChangelogStore } from '@entities/changelog'
import { pick } from '@shared/lib/collections'
import { useDayjs } from '@shared/lib/format'

const DATE_TIME_FORMAT = DateFormat.Default

interface FiltersChangelogReturn {
  refSearch: WritableComputedRef<string>
  refDateRange: WritableComputedRef<Date[] | null>
  isLoading: ComputedRef<boolean>
  handleSearch: () => Promise<void>
  handleClear: () => Promise<void>
}

export const useFiltersChangelog = (): FiltersChangelogReturn => {
  const router = useRouter()
  const store = useChangelogStore()
  const dayjs = useDayjs()

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => store.setFilterSearch(value.trim() || null),
  })

  const refDateRange = computed<Date[] | null>({
    get: () => {
      if (!store.getFilterFrom || !store.getFilterTo) {
        return null
      }
      return [dayjs(store.getFilterFrom).toDate(), dayjs(store.getFilterTo).toDate()]
    },
    set: (value) => {
      if (value && value.length === 2 && value[0] && value[1]) {
        store.setFilterFrom(dayjs(value[0]).format(DATE_TIME_FORMAT))
        store.setFilterTo(dayjs(value[1]).format(DATE_TIME_FORMAT))
      } else {
        store.setFilterFrom(null)
        store.setFilterTo(null)
      }
    },
  })

  const isLoading = computed<boolean>(() => store.isLoading)

  const handleSearch = async (): Promise<void> => {
    store.setCursor(null)
    await router.push({
      query: pick({
        query: store.getFilterSearch ?? undefined,
        from: store.getFilterFrom ?? undefined,
        to: store.getFilterTo ?? undefined,
        per_page: String(store.getPerPage),
      }),
    })
    await useChangelogApi()
  }

  const handleClear = async (): Promise<void> => {
    store.resetFilters()
    await router.push({ query: {} })
    await useChangelogApi()
  }

  return {
    refSearch,
    refDateRange,
    isLoading,
    handleSearch,
    handleClear,
  }
}
